import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getImageGenerationLockEventName, type ImageGenerationLockDetail } from '../../lib/imageGenerationLock';

const guardMessage = 'Image generation is in progress. Please wait before leaving this page.';
const activeMessage = 'Generating your image, please wait...';

export default function ImageGenerationNavigationGuard() {
  const [locked, setLocked] = useState(false);
  const [blockedNotice, setBlockedNotice] = useState(false);
  const currentUrlRef = useRef('');

  useEffect(() => {
    const onLockChange = (event: Event) => {
      const detail = (event as CustomEvent<ImageGenerationLockDetail>).detail;
      setLocked(Boolean(detail?.active));
    };

    window.addEventListener(getImageGenerationLockEventName(), onLockChange);
    return () => window.removeEventListener(getImageGenerationLockEventName(), onLockChange);
  }, []);

  useEffect(() => {
    if (!blockedNotice) return;
    const timeout = window.setTimeout(() => setBlockedNotice(false), 2600);
    return () => window.clearTimeout(timeout);
  }, [blockedNotice]);

  useEffect(() => {
    if (!locked) return;

    currentUrlRef.current = window.location.href;
    window.history.pushState({ imageGenerationGuard: true }, '', currentUrlRef.current);

    const showBlockedNotice = () => setBlockedNotice(true);

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = guardMessage;
      return guardMessage;
    };

	    const onClick = (event: MouseEvent) => {
	      const target = event.target instanceof Element ? event.target : null;
	      const interactive = target?.closest('a[href], button, input, select, textarea, [role="button"]');
	      if (!interactive) return;

	      const href = interactive instanceof HTMLAnchorElement ? interactive.getAttribute('href') || '' : '';
	      if (href && href.startsWith('#')) return;

	      if (href) {
	        const url = new URL(href, window.location.href);
	        if (url.origin !== window.location.origin) return;
	      }

	      event.preventDefault();
	      event.stopPropagation();
	      showBlockedNotice();
	    };

	    const onKeyDown = (event: KeyboardEvent) => {
	      const lowerKey = event.key.toLowerCase();
	      const shouldBlock =
	        event.key === 'Enter' ||
	        event.key === ' ' ||
	        event.key === 'Escape' ||
	        event.key === 'Backspace' ||
	        event.key === 'F5' ||
	        ((event.metaKey || event.ctrlKey) && ['r', 'l', 'w', 'arrowleft', 'arrowright'].includes(lowerKey)) ||
	        (event.altKey && ['arrowleft', 'arrowright'].includes(lowerKey));

	      if (!shouldBlock) return;

	      event.preventDefault();
	      event.stopPropagation();
	      showBlockedNotice();
	    };

    const onPopState = () => {
      window.history.pushState({ imageGenerationGuard: true }, '', currentUrlRef.current);
      showBlockedNotice();
    };

	    window.addEventListener('beforeunload', onBeforeUnload);
	    window.addEventListener('click', onClick, true);
	    window.addEventListener('keydown', onKeyDown, true);
	    window.addEventListener('popstate', onPopState);

	    return () => {
	      window.removeEventListener('beforeunload', onBeforeUnload);
	      window.removeEventListener('click', onClick, true);
	      window.removeEventListener('keydown', onKeyDown, true);
	      window.removeEventListener('popstate', onPopState);
	    };
	  }, [locked]);

  if (!locked && !blockedNotice) return null;

	  if (locked) {
	    return (
	      <div className="fixed inset-0 z-[80] flex cursor-wait items-center justify-center bg-slate-950/20 px-4 backdrop-blur-[1px]">
	        <div className="w-full max-w-md rounded-xl border border-sky-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-xl">
	          <div className="flex items-center gap-3">
	            <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-sky-600" />
	            <span>{blockedNotice ? guardMessage : activeMessage}</span>
	          </div>
	        </div>
	      </div>
	    );
	  }

	  return (
	    <div className="fixed bottom-5 left-1/2 z-[80] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-xl border border-sky-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-xl">
	      <div className="flex items-center gap-3">
	        <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-sky-600" />
	        <span>{guardMessage}</span>
	      </div>
	    </div>
	  );
	}
